import {useAppDispatch, useAppSelector} from '@/store'
import {setPointMultiplierFormData} from '@/store/slices/pointMultiplier'
import {setRuleSet} from '@/store/slices/salesTargetSlice'
import {ROUTES} from '@/utils/constants/routesConstants'
import {TARGET_TYPES} from '@/utils/constants/saleTargetConstants'

export const useCSVFileValidation = () => {
  const value = []
  const errors = []
  const dispatch = useAppDispatch()
  const salesTarget = useAppSelector(state => state.salesTarget)

  // Define constants outside switch statement
  const alphanumericRegex = /^[a-z0-9]+$/i

  const validateFieldType = (type, columnValue, columnName, rowIndex, minChar, maxChar, rawValue) => {
    switch (type) {
      case 'string': {
        if (typeof rawValue !== 'string') {
          errors.push(`Row ${rowIndex + 2}: '${columnName}' should be a string.`)
        }
        if (columnValue && (columnValue.length < minChar || columnValue.length > maxChar)) {
          errors.push(`Row ${rowIndex + 2}: '${columnName}' should be between ${minChar} and ${maxChar} characters.`)
        }
        break
      }
      case 'number': {
        if (isNaN(Number(columnValue))) {
          errors.push(`Row ${rowIndex + 2}: '${columnName}' should be a numerical value.`)
        }
        break
      }
      case 'alphanumeric': {
        if (!alphanumericRegex.test(columnValue)) {
          errors.push(`Row ${rowIndex + 2}: '${columnName}' should be an alphanumeric string.`)
        }
        if (columnValue && (columnValue.length < minChar || columnValue.length > maxChar)) {
          errors.push(`Row ${rowIndex + 2}: '${columnName}' should be between ${minChar} and ${maxChar} characters.`)
        }
        break
      }
    }
  }

  const validateCSVFile = (fileValidationRules, fileObj, desiredKey, route = false) => {
    const {type, size, columns, requiredHeaders} = fileValidationRules
    const {fileType, csvData, fileSize} = fileObj
    const validHeaders = columns.map(column => column.name)
    // Validate file type and size
    if (type !== fileType) {
      errors.push(`file type should be ${type}`)
    }
    if (fileSize > size) {
      errors.push(`file size should be less than or equal to ${size} KB`)
    }
    // Clean and validate CSV data
    const cleanedData = csvData
      ?.filter(row => {
        return Object.values(row).some(val => val !== undefined && val !== null && String(val).trim() !== '')
      })
      .map(row => {
        const cleanRow = {}
        Object.entries(row).forEach(([key, val]) => {
          key = String(key).trim()
          cleanRow[key] = val !== undefined && val !== null ? String(val).trim() : ''
        })
        return cleanRow
      })
    if (!cleanedData?.length) {
      errors.push('CSV file contains no valid data')
      return {isInvalidFileData: true, errorsList: errors}
    }

    const headers = Object.keys(cleanedData[0])
    if (validHeaders?.length > 0) {
      const unexpectedHeaders = headers.filter(header => !validHeaders.includes(header))
      const validUnexpectedHeaders = unexpectedHeaders.filter(header => {
        return header.trim() !== '' && !header.startsWith('_')
      })
      if (validUnexpectedHeaders.length > 0) {
        errors.push(`Unexpected column(s) found: ${validUnexpectedHeaders.join(', ')}`)
      }
    }

    // Validate headers
    if (requiredHeaders?.length > 0) {
      const missingHeaders = requiredHeaders.filter(header => !headers.includes(header))
      if (missingHeaders.length > 0) {
        errors.push(`Missing required column(s): ${missingHeaders.join(', ')}`)
      }
    }
    cleanedData.forEach((row, rowIndex) => {
      if (desiredKey && row[desiredKey]) {
        value.push(row[desiredKey].trim())
      }

      columns?.forEach(column => {
        const {name: columnName, type, required, minChar, maxChar, unique} = column
        const rawValue = row[columnName]
        const columnValue = rawValue?.trim()

        if (required && !columnValue) {
          errors.push(`Row ${rowIndex + 2}: '${columnName}' is required.`)
          return
        }
        if(unique===true){
          const existingValues = value.filter(v => v === columnValue)
          if (existingValues.length > 1) {
            errors.push(`Row ${rowIndex + 2}: '${columnName}' must be unique. Found duplicate value: ${columnValue}.`)
            return
          }
        }

        if (!columnValue && !required) return

        validateFieldType(type, columnValue, columnName, rowIndex, minChar, maxChar, rawValue)
      })
    })

    // Prepare and dispatch rule set
    const targetName = salesTarget?.targetType === TARGET_TYPES.MainTarget
    const ruleSet = {
      includeAllProductsByDefault: targetName,
      skipNonAssorted: false,
      rules: [
        {
          includedProductIds: value?.map(v => v?.toString()),
          includedCategories: [],
          excludedCategories: [],
          excludedProductIds: [],
        },
      ],
    }
    if (!route) dispatch(setRuleSet({ruleSet}))
    else if (route == ROUTES.POINT_MULTIPLIER_PRODUCTS) {
      const toCamelCase = str => {
        return str
          .toLowerCase()
          .split(/[\s]+/)
          .map((word, index) => {
            if (index === 0) return word
            return word.charAt(0).toUpperCase() + word.slice(1)
          })
          .join('')
      }
      const productMultipliers = cleanedData.map(row => {
        Object.keys(row).forEach(key => {
          const newKey = toCamelCase(key)
          if (newKey !== key) {
            row[newKey] = row[key]
            delete row[key]
          }
        })
        return row
      })
      dispatch(setPointMultiplierFormData({productMultipliers}))
    }
    return {isInvalidFileData: errors.length > 0, errorsList: errors}
  }

  return {validateCSVFile}
}
